import { computed, effect, inject, Injectable, resource } from '@angular/core';
import { ApiConfig } from 'src/app/shared/api-config/api-config';
import { untilAborted } from 'src/app/shared/dashboard-grid';
import { LogHelper } from 'src/app/shared/logging/log-helper';
import { MetricDefinition, MetricsApi } from 'src/app/usage-api';

/** The Usage API's Kind for a Metric read as its current value, such as rooms in use */
const GAUGE_KIND = 'gauge';

/**
 * The tenant's Usage Metric Catalog, loaded once per page load, on first use. Each Kind has its own list, such as
 * `gauges()`; the catalog already holds only the Metrics the caller can read.
 */
@Injectable({ providedIn: 'root' })
export class MetricCatalog {
  private readonly metrics = inject(MetricsApi);
  private readonly catalog = resource({
    loader: ({ abortSignal }) => untilAborted(this.metrics.metricsGetCatalog(ApiConfig.tenantId), abortSignal),
  });

  /** The catalog is in hand, so a Metric it doesn't list is one the caller can't read, or that no longer exists */
  readonly loaded = computed(() => this.catalog.hasValue());

  /** Every Gauge, deprecated ones included; empty while loading or when the catalog fails to load */
  private readonly allGauges = computed((): MetricDefinition[] =>
    (this.catalog.hasValue() ? this.catalog.value() : []).filter((metric) => metric.kind === GAUGE_KIND && !!metric.id),
  );

  /** The Gauges to offer for new tiles */
  readonly gauges = computed(() => this.allGauges().filter((gauge) => !gauge.deprecated));

  constructor() {
    effect(() => {
      if (this.catalog.status() === 'error') {
        LogHelper.error('MetricCatalog', 'Failed to load the Usage Metric Catalog', this.catalog.error());
      }
    });
  }

  /** Finds deprecated Gauges too, so a tile saved before its Gauge was deprecated keeps the Gauge's name */
  findGauge(id: string): MetricDefinition | undefined {
    return this.allGauges().find((gauge) => gauge.id === id);
  }
}
